// 🗑️ 삭제 확인 다이얼로그 컴포넌트
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  Button,
  Avatar,
  Box,
  Stack,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Application } from '../../../../types/application';

interface DeleteConfirmDialogProps {
  open: boolean;
  application: Application;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * 애플리케이션 삭제 확인 다이얼로그 컴포넌트
 * - 삭제 확인 및 경고 메시지
 * - 삭제될 정보 미리보기
 * - 취소 및 확인 버튼
 */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  application,
  isDeleting,
  onClose,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          color: 'error.main',
          fontWeight: 700,
          fontSize: '1.25rem',
          pb: 1,
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'error.light',
            color: 'white',
            width: 48,
            height: 48,
          }}
        >
          <WarningIcon />
        </Avatar>
        신청서 삭제 확인
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <DialogContentText sx={{ fontSize: '1rem', lineHeight: 1.6, mb: 2 }}>
          <strong>#{application.application_number}</strong> 신청서를 <strong style={{ color: '#d32f2f' }}>영구적으로 삭제</strong>하시겠습니까?
        </DialogContentText>
        
        <Box
          sx={{
            p: 2,
            bgcolor: 'error.50',
            borderRadius: 2,
            border: '1px solid #ffcdd2',
          }}
        >
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight={600} color="error.main">
              ⚠️ 삭제 시 다음 정보가 모두 사라집니다:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              • 신청자 정보 (이름: {application.user_name})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              • 기기 정보 ({application.devices?.length || 0}개 기기)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              • 처리 이력 및 관리자 메모
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              • 알림톡 발송 이력
            </Typography>
          </Stack>
        </Box>

        <Typography 
          variant="body2" 
          color="error.main" 
          fontWeight={600}
          sx={{ mt: 2, textAlign: 'center' }}
        >
          이 작업은 되돌릴 수 없습니다!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isDeleting}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          취소
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <SaveIcon /> : <DeleteIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
            },
          }}
        >
          {isDeleting ? '삭제 중...' : '영구 삭제'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};